import type { MfaChallenge } from '@revival/contracts';
import type { AcceptInvitation, CreateContractor, CreateProject, Evidence, InvitationPreview, Project, Session, Snag, SyncPush, User } from "@revival/contracts";
import type { AddComment, SnagComment, EvidenceKind } from '@revival/contracts';
import type { NotificationFeed, NotificationQuery } from '@revival/contracts';
import { cacheValue, cachedValue, disconnected, forgetCached, hasCached, isNetworkFailure, NetworkError } from './offline-cache.js';

export type Bootstrap = {
  project: Project;
  currentContractorCompanyId: string | null;
  snags: Snag[];
  contractorCompanies: Array<{
    id: string; organisationId: string; company: string; trade: string; contactName: string;
    telephone: string; email: string; accountCount: number; fullSnagListReadOnly: boolean; active: boolean;
  }>;
  locations: Array<{ id: string; organisationId: string; projectId: string; name: string; active: boolean }>;
  drawings: Array<{ id: string; projectId: string; title: string; revision: string; fileName: string; contentType: string; sizeBytes: number; superseded: boolean }>;
  evidence: Evidence[];
  commentAttention?: string[];
  cursor: number;
};

export type OfflineProjectState = {
  ready: boolean; dataReady: boolean; saved: number; total: number;
  savedBytes: number; totalBytes: number; skippedVideos: number; updatedAt: string;
};

export type ContractorDirectoryRecord = {
  id: string; organisationId: string; company: string; trade: string; contactName: string;
  telephone: string; email: string; accountCount: number; fullSnagListReadOnly: boolean;
  active: boolean; deletedAt: string | null; projectCount: number; snagCount: number;
};

export type InvitationHistoryRecord = {
  id: string; projectId: string | null; contractorCompanyId: string | null; email: string | null;
  firstName: string; lastName: string; telephone: string; role: "manager" | "contractor";
  kind: "company" | "individual"; expiresAt: string; acceptedAt: string | null; revokedAt: string | null;
  useCount: number; lastUsedAt: string | null; createdAt: string; projectName: string | null;
  company: string | null; trade: string | null;
};

export type UserHistoryRecord = { user: User; projects: Array<Project & { relationship: string }>; availableProjects: Array<Project & { assigned: boolean; required: boolean }>; invitations: InvitationHistoryRecord[] };
export type ContractorHistoryRecord = { contractor: ContractorDirectoryRecord; projects: Array<Project & { snagCount: number; fullSnagListReadOnly: boolean }>; users: User[]; invitations: InvitationHistoryRecord[] };
export type ProjectContractorAccount = User & { projectAccess: boolean; fullSnagListReadOnly: boolean };
export type AccountChangeRecord = { id: string; userId: string | null; userName: string; userEmail: string; actorId: string | null; actorName?: string; kind: string; beforeValues: Record<string, unknown>; afterValues: Record<string, unknown>; occurredAt: string };

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string, public account?: User) { super(message); }
}

export class ApiClient {
  private accessToken = sessionStorage.getItem("site-audit-access-token") ?? "";
  private refreshToken = sessionStorage.getItem("site-audit-refresh-token") ?? "";
  private sessionGeneration = 0;
  private identityEpoch = 0;
  private viewAsUserId = "";
  isViewingAs() { return Boolean(this.viewAsUserId); }
  workOwner(userId: string) { return this.viewAsUserId ? `view-as|${this.ownerId}|${this.viewAsUserId}` : userId; }
  async startViewAs(userId: string): Promise<User> {
    const user = await this.request<User>(`/v1/admin/view-as/${encodeURIComponent(userId)}`);
    this.viewAsUserId = user.id; this.identityEpoch += 1;
    this.warming.clear(); this.warmRetryAt.clear(); this.offlinePaths.clear();
    return user;
  }
  endViewAs() { this.viewAsUserId = ""; this.identityEpoch += 1; this.offlinePaths.clear(); }
  private previewHeaders(): Record<string,string> { return this.viewAsUserId ? { 'x-site-view-as': this.viewAsUserId } : {}; }
  private ownerId = sessionStorage.getItem('site-audit-cache-owner') ?? '';
  private warming = new Set<string>();
  private preparingProjects: Promise<void> | null = null;
  private warmingProjects = new Set<string>();
  private warmRetryAt = new Map<string, number>();
  private offlinePaths = new Set<string>();
  usingOfflineData() { return this.offlinePaths.size > 0; }
  beginDataRefresh() { this.offlinePaths.clear(); }
  sessionIdentity() { return this.identityEpoch; }
  currentOwner() { return this.ownerId; }
  private refreshInFlight: Promise<boolean> | null = null;

  isSignedIn(): boolean {
    return Boolean(this.accessToken);
  }

  serverBase(): string {
    return API_URL.startsWith("http") ? API_URL : `${window.location.origin}${API_URL}`;
  }

  signOut(): void {
    this.sessionGeneration += 1; this.identityEpoch += 1;
    this.viewAsUserId = "";
    this.accessToken = "";
    this.refreshToken = "";
    this.ownerId = ''; this.warming.clear(); this.warmingProjects.clear(); this.offlinePaths.clear();
    sessionStorage.removeItem('site-audit-cache-owner');
    sessionStorage.removeItem("site-audit-access-token");
    sessionStorage.removeItem("site-audit-refresh-token");
  }

  async login(email: string, password: string): Promise<Session> {
    const result = await this.beginLogin(email, password);
    if ('mfaRequired' in result) throw new ApiError('Authenticator verification is required. Use the sign-in screen.', 401, 'MFA_REQUIRED');
    return result;
  }
  async beginLogin(email: string, password: string): Promise<Session | MfaChallenge> {
    const session = await this.request<Session | MfaChallenge>("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        deviceId: crypto.randomUUID(),
        deviceName: "Site Snag web app",
      }),
    }, false);
    if ('mfaRequired' in session) return session;
    this.sessionGeneration += 1; this.identityEpoch += 1;
    this.accessToken = session.accessToken;
    this.refreshToken = session.refreshToken;
    sessionStorage.setItem("site-audit-access-token", session.accessToken);
    sessionStorage.setItem("site-audit-refresh-token", session.refreshToken);
    await this.rememberUser(session.user);
    return session;
  }

  async completeMfa(challenge: string, code: string): Promise<Session & { recoveryCodes?: string[] }> {
    const session = await this.request<Session & { recoveryCodes?: string[] }>('/v1/auth/mfa', { method: 'POST', body: JSON.stringify({ challenge, code }) }, false);
    this.sessionGeneration += 1; this.identityEpoch += 1;
    this.accessToken = session.accessToken; this.refreshToken = session.refreshToken;
    sessionStorage.setItem('site-audit-access-token', session.accessToken);
    sessionStorage.setItem('site-audit-refresh-token', session.refreshToken);
    await this.rememberUser(session.user);
    return session;
  }
  platformLicence() { return this.request<{ managed: boolean; status?: string; write?: boolean; message?: string; revision?: number }>('/v1/platform/licence'); }
  customerExport() { return this.file('/v1/platform/export'); }
  supportInvitations() { return this.request<Array<{ id: string; name: string; email: string; projectIds: string[]; expiresAt: string; acceptedAt: string | null; revokedAt: string | null }>>('/v1/platform/support-invitations'); }
  inviteSupport(input: { name: string; email: string; projectIds: string[]; expiresAt: string; reason: string }) { return this.request<{ id: string; token: string; expiresAt: string }>('/v1/platform/support-invitations', { method: 'POST', body: JSON.stringify(input) }); }
  revokeSupport(id: string, reason: string) { return this.request(`/v1/platform/support-invitations/${encodeURIComponent(id)}/revoke`, { method: 'POST', body: JSON.stringify({ reason }) }); }
  platformInvitation(token: string) { return this.request<{ name: string; email: string; kind: string }>('/v1/platform/invitation/preview', { method: 'POST', body: JSON.stringify({ token }) }, false); }
  acceptPlatformInvitation(token: string, password: string) { return this.request('/v1/platform/invitation/accept', { method: 'POST', body: JSON.stringify({ token, password }) }, false); }

  async acceptInvitation(input: Omit<AcceptInvitation, "deviceId" | "deviceName">): Promise<Session> {
    const session = await this.request<Session>("/v1/invitations/accept", {
      method: "POST",
      body: JSON.stringify({ ...input, deviceId: crypto.randomUUID(), deviceName: "Site Snag web app" }),
    }, false);
    this.sessionGeneration += 1; this.identityEpoch += 1;
    this.accessToken = session.accessToken;
    this.refreshToken = session.refreshToken;
    sessionStorage.setItem("site-audit-access-token", session.accessToken);
    sessionStorage.setItem("site-audit-refresh-token", session.refreshToken);
    await this.rememberUser(session.user);
    return session;
  }

  private async rememberUser(user: User) {
    if(this.ownerId !== user.id) {this.warming.clear();this.warmRetryAt.clear();this.offlinePaths.clear();}
    this.ownerId = user.id; sessionStorage.setItem('site-audit-cache-owner', user.id);
    await cacheValue(user.id, '/v1/me', user);
  }
  async me(onlineOnly = false): Promise<User> {
    if (this.isViewingAs()) return this.request<User>('/v1/me');
    const identity = this.identityEpoch;
    try {
      const user = await this.request<User>('/v1/me'); this.offlinePaths.delete('/v1/me');
      if (identity === this.identityEpoch) await this.rememberUser(user);
      return user;
    } catch (reason) {
      if (!onlineOnly && this.isSignedIn() && isNetworkFailure(reason)) {
        const user = await cachedValue<User>(this.ownerId, '/v1/me');
        if (user && identity === this.identityEpoch) {this.offlinePaths.add('/v1/me'); return user;}
      }
      throw reason;
    }
  }
  private async offlineRead<T>(path: string): Promise<T> {
    if (this.isViewingAs()) return this.request<T>(path);
    const owner = this.ownerId, identity = this.identityEpoch;
    try {
      const value = await this.request<T>(path); this.offlinePaths.delete(path);
      if (owner && identity === this.identityEpoch) await cacheValue(owner, path, value);
      return value;
    } catch (reason) {
      if (identity === this.identityEpoch && reason instanceof ApiError && [401, 403, 404].includes(reason.status)) await forgetCached(owner, path);
      if (this.isSignedIn() && isNetworkFailure(reason) && identity === this.identityEpoch) {
        const value = await cachedValue<T>(owner, path);
        if (value !== null && identity === this.identityEpoch) { if(path === '/v1/projects' || path.endsWith('/bootstrap')) this.offlinePaths.add(path); return value; }
      }
      throw reason;
    }
  }

  presentation(kind: string, id: string): Promise<{ displayName: string; mediaId: string | null; shape: "circle" | "square" }> {
    return this.request(`/v1/presentation/${kind}/${id}`);
  }
  presentationBlob(kind: string, id: string): Promise<Blob> { return this.file(`/v1/presentation/${kind}/${id}/image`); }
  uploadPresentation(kind: string, id: string, file: File, shape: "circle" | "square" = "circle"): Promise<{ mediaId: string }> {
    const form = new FormData(); form.append('shape', shape); form.append('file', file);
    return this.request(`/v1/presentation/${kind}/${id}/image`, { method: 'POST', body: form });
  }
  removePresentation(kind: string, id: string): Promise<{ removed: boolean }> { return this.request(`/v1/presentation/${kind}/${id}/image`, { method: 'DELETE' }); }
  setOrganisationName(name: string): Promise<{ name: string }> { return this.request('/v1/organisation/name', { method: 'PATCH', body: JSON.stringify({ name }) }); }

  updateProfile(name: string, telephone: string): Promise<User> {
    return this.request<User>("/v1/profile", { method: "PATCH", body: JSON.stringify({ name, telephone }) });
  }

  changePassword(password: string): Promise<User> {
    return this.request<User>("/v1/profile/change-password", { method: "POST", body: JSON.stringify({ password }) });
  }

  async ownContractorCompany(): Promise<{ company: string; trade: string } | null> {
    const result = await this.request<{ company: { company: string; trade: string } | null }>("/v1/profile/company");
    return result.company;
  }

  deleteOwnProfile(): Promise<{ archived: boolean }> {
    return this.request<{ archived: boolean }>("/v1/profile", { method: "DELETE" });
  }

  async projects(): Promise<Project[]> {
    if (this.isViewingAs()) return (await this.request<{ projects: Project[] }>('/v1/projects')).projects;
    const previous = await cachedValue<{ projects: Project[] }>(this.ownerId, '/v1/projects');
    const result = await this.offlineRead<{ projects: Project[] }>("/v1/projects");
    for (const project of previous?.projects ?? []) if (!result.projects.some(p => p.id === project.id)) await forgetCached(this.ownerId, `/v1/projects/${project.id}/bootstrap`);
    return result.projects;
  }

  createProject(input: CreateProject): Promise<Project> {
    return this.request<Project>("/v1/projects", { method: "POST", body: JSON.stringify(input) });
  }

  updateProject(projectId: string, input: CreateProject): Promise<Project> {
    return this.request<Project>(`/v1/projects/${projectId}`, { method: "PATCH", body: JSON.stringify(input) });
  }

  setProjectStatus(projectId: string, status: Project["status"]): Promise<Project> {
    return this.request<Project>(`/v1/projects/${projectId}/status`, { method: "PATCH", body: JSON.stringify({ status }) });
  }

  setProjectManagers(projectId: string, managerAccessAll: boolean, managerIds: string[]): Promise<{ saved: boolean }> {
    return this.request<{ saved: boolean }>(`/v1/projects/${projectId}/managers`, { method: "PATCH", body: JSON.stringify({ managerAccessAll, managerIds }) });
  }

  async archivedProjects(): Promise<Project[]> {
    return (await this.request<{ projects: Project[] }>("/v1/archive/projects")).projects;
  }

  archivedProjectView(projectId: string): Promise<Bootstrap> { return this.request(`/v1/archive/projects/${projectId}/view`); }
  updateDrawingReference(snagId:string, input:{drawingId:string;drawingPage:number;markupX:number;markupY:number;expectedVersion:number}):Promise<Snag> {
    return this.request(`/v1/snags/${snagId}/drawing-reference`,{method:'PATCH',body:JSON.stringify(input)});
  }
  bootstrap(projectId: string): Promise<Bootstrap> {
    return this.offlineRead<Bootstrap>(`/v1/projects/${projectId}/bootstrap`);
  }
  // One background job across all accessible projects, at most two file requests
  // at a time. Leaving a project must not cancel the next project's download.
  warmProjects(projects: Bootstrap[]): Promise<void> {
    if (this.isViewingAs()) return Promise.resolve();
    if (this.preparingProjects) return this.preparingProjects;
    const identity = this.identityEpoch;
    const work = (async () => {
      for (const project of projects) {
        if (identity !== this.identityEpoch || disconnected()) break;
        await this.warmProject(project);
      }
    })();
    this.preparingProjects = work;
    void work.finally(() => { if (this.preparingProjects === work) this.preparingProjects = null; }).catch(() => undefined);
    return work;
  }
  async warmProject(data: Bootstrap): Promise<void> {
    if (this.isViewingAs()) return;
    const projectKey = this.ownerId + ':' + data.project.id;
    if (disconnected() || !this.ownerId || this.warmingProjects.has(projectKey) || (this.warmRetryAt.get(projectKey) ?? 0) > Date.now()) return;
    this.warmingProjects.add(projectKey);
    const identity = this.identityEpoch, owner = this.ownerId;
    const isVideo = (item: Evidence) => item.kind === 'issue_video' || item.contentType.startsWith('video/');
    const files = [
      ...data.evidence.filter(item => !isVideo(item)).map(item => ({ key: `evidence:${item.id}`, path: `/v1/evidence/${item.id}/file`, bytes: item.sizeBytes, load: () => this.evidenceBlob(item.id) })),
      // Include superseded drawings still referenced by a snag.
      ...data.drawings.filter(item => !item.superseded || data.snags.some(snag => snag.drawingId === item.id)).map(item => ({ key: `drawing:${item.id}`, path: `/v1/drawings/${item.id}/file`, bytes: item.sizeBytes, load: () => this.drawingBlob(item.id) })),
    ];
    const comments = data.snags.map(item => ({ key: `comments:${item.id}:${data.cursor}:${item.updatedAt}`, path: `/v1/snags/${item.id}/comments`, load: () => this.snagComments(item.id) }));
    // Notes before media: the snag workflow should remain usable on a slow link.
    const tasks = [...comments, ...files];
    let next = 0, failed = 0;
    const publish = async () => {
      if (identity !== this.identityEpoch) return;
      const saved = await Promise.all(files.map(task => hasCached(owner, task.path)));
      const dataReady = (await Promise.all(['/v1/me', '/v1/projects', `/v1/projects/${data.project.id}/bootstrap`].map(path => hasCached(owner, path)))).every(Boolean);
      const notesReady = (await Promise.all(comments.map(task => hasCached(owner, task.path)))).every(Boolean);
      const state: OfflineProjectState = { ready: dataReady && notesReady && saved.every(Boolean), dataReady,
        saved: saved.filter(Boolean).length, total: files.length,
        savedBytes: files.reduce((sum, file, index) => sum + (saved[index] ? file.bytes : 0), 0),
        totalBytes: files.reduce((sum, file) => sum + file.bytes, 0), skippedVideos: data.evidence.filter(isVideo).length, updatedAt: new Date().toISOString() };
      await cacheValue(owner, `prepared:${data.project.id}`, state);
      if (identity === this.identityEpoch) window.dispatchEvent(new Event('offline-project-prepared'));
    };
    try {
      await publish();
      await Promise.all(Array.from({ length: 2 }, async () => {
      while (next < tasks.length && identity === this.identityEpoch && !disconnected()) {
        const task = tasks[next++]!;
        if (this.warming.has(task.key) && await hasCached(owner, task.path)) continue;
        try { await task.load(); if(!await hasCached(owner, task.path)) throw new Error('Browser storage unavailable'); this.warming.add(task.key); }
        catch { this.warming.delete(task.key); failed++; }
      }
    }));
      if(identity === this.identityEpoch) {
        await publish();
        if (failed) this.warmRetryAt.set(projectKey, Date.now() + 60_000);
      }
    } finally { this.warmingProjects.delete(projectKey); }
  }

  createLocation(projectId: string, name: string) {
    return this.request<{ id: string; name: string }>(`/v1/projects/${projectId}/locations`, { method: "POST", body: JSON.stringify({ name }) });
  }

  createContractor(projectId: string, input: CreateContractor) {
    return this.request<{ id: string; company: string; trade: string; contactName: string; telephone: string; email: string; accountCount: number; fullSnagListReadOnly: boolean }>(`/v1/projects/${projectId}/contractors`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  setContractorAccess(projectId: string, contractorCompanyId: string, fullSnagListReadOnly: boolean) {
    return this.request(`/v1/projects/${projectId}/contractors/${contractorCompanyId}/access`, {
      method: "PATCH",
      body: JSON.stringify({ fullSnagListReadOnly }),
    });
  }

  async projectContractorAccounts(projectId: string, contractorCompanyId: string): Promise<ProjectContractorAccount[]> {
    return (await this.request<{ users: ProjectContractorAccount[] }>(`/v1/projects/${projectId}/contractors/${contractorCompanyId}/accounts`)).users;
  }

  setProjectContractorAccountAccess(projectId: string, userId: string, active: boolean, fullSnagListReadOnly = false): Promise<ProjectContractorAccount> {
    return this.request<ProjectContractorAccount>(`/v1/projects/${projectId}/contractor-users/${userId}/access`, {
      method: "PATCH",
      body: JSON.stringify({ active, fullSnagListReadOnly }),
    });
  }

  resetProjectContractorPassword(projectId: string, userId: string): Promise<{ temporaryPassword: string }> {
    return this.request<{ temporaryPassword: string }>(`/v1/projects/${projectId}/contractor-users/${userId}/reset-password`, { method: "POST", body: JSON.stringify({}) });
  }

  removeContractor(projectId: string, contractorCompanyId: string) {
    return this.request<{ removed: boolean }>(`/v1/projects/${projectId}/contractors/${contractorCompanyId}`, { method: "DELETE" });
  }

  inviteContractor(projectId: string, input: { contractorCompanyId: string; kind: "company" | "individual"; firstName?: string; lastName?: string; email?: string; telephone?: string }) {
    return this.request<{ token: string; expiresAt: string; contractor: { id: string; company: string; trade: string } }>(`/v1/projects/${projectId}/contractor-invitations`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  previewInvitation(token: string): Promise<InvitationPreview> {
    return this.request<InvitationPreview>(`/v1/invitations/${encodeURIComponent(token)}/preview`, {}, false);
  }

  inviteManager(email: string) {
    return this.request<{ token: string; expiresAt: string }>("/v1/manager-invitations", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async users(): Promise<User[]> {
    return (await this.request<{ users: User[] }>("/v1/users")).users;
  }

  setUserActive(userId: string, active: boolean): Promise<User> {
    return this.request<User>(`/v1/users/${userId}`, { method: "PATCH", body: JSON.stringify({ active }) });
  }

  permanentlyDeleteUser(userId: string): Promise<{ archived: boolean }> {
    return this.request<{ archived: boolean }>(`/v1/users/${userId}`, { method: "DELETE" });
  }

  restoreUser(userId: string): Promise<User> {
    // Explicit JSON keeps this action valid through proxies that stream an
    // otherwise empty PATCH using chunked framing.
    return this.request<User>(`/v1/users/${userId}/restore`, { method: "PATCH", body: JSON.stringify({}) });
  }

  purgeArchivedUser(userId: string, confirmEmail: string): Promise<{ deleted: boolean }> {
    return this.request(`/v1/archive/users/${userId}`, { method: "DELETE", body: JSON.stringify({ confirmEmail }) });
  }

  purgeArchivedCompany(companyId: string, confirmCompany: string): Promise<{ deleted: boolean }> {
    return this.request(`/v1/archive/companies/${companyId}`, { method: "DELETE", body: JSON.stringify({ confirmCompany }) });
  }

  deleteAccountChanges(ids: string[]): Promise<{ deleted: number }> {
    return this.request("/v1/account-changes", { method: "DELETE", body: JSON.stringify({ ids, confirmCount: new Set(ids).size }) });
  }

  resetUserPassword(userId: string): Promise<{ temporaryPassword: string }> {
    return this.request<{ temporaryPassword: string }>(`/v1/users/${userId}/reset-password`, { method: "POST", body: JSON.stringify({}) });
  }

  setUserProjects(userId: string, projectIds: string[]): Promise<{ saved: boolean }> {
    return this.request<{ saved: boolean }>(`/v1/users/${userId}/projects`, { method: "PATCH", body: JSON.stringify({ projectIds }) });
  }

  userHistory(userId: string): Promise<UserHistoryRecord> {
    return this.request<UserHistoryRecord>(`/v1/users/${userId}/history`);
  }

  async accountChanges(search = "", offset = 0, range: { from?: string; to?: string } = {}): Promise<AccountChangeRecord[]> {
    const query = new URLSearchParams({ search, offset: String(offset) });
    if (range.from) query.set("from", range.from);
    if (range.to) query.set("to", range.to);
    return (await this.request<{ changes: AccountChangeRecord[] }>(`/v1/account-changes?${query}`)).changes;
  }

  async contractorDirectory(): Promise<ContractorDirectoryRecord[]> {
    return (await this.request<{ contractors: ContractorDirectoryRecord[] }>("/v1/contractors")).contractors;
  }

  saveContractor(input: CreateContractor, contractorCompanyId?: string): Promise<ContractorDirectoryRecord> {
    return this.request<ContractorDirectoryRecord>(contractorCompanyId ? `/v1/contractors/${contractorCompanyId}` : "/v1/contractors", {
      method: contractorCompanyId ? "PATCH" : "POST",
      body: JSON.stringify(input),
    });
  }

  setContractorActive(contractorCompanyId: string, active: boolean): Promise<ContractorDirectoryRecord> {
    return this.request<ContractorDirectoryRecord>(`/v1/contractors/${contractorCompanyId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ active }),
    });
  }

  permanentlyDeleteContractor(contractorCompanyId: string): Promise<{ archived: boolean }> {
    return this.request<{ archived: boolean }>(`/v1/contractors/${contractorCompanyId}`, { method: "DELETE" });
  }

  mergeContractor(sourceId: string, targetContractorCompanyId: string): Promise<ContractorDirectoryRecord> {
    return this.request<ContractorDirectoryRecord>(`/v1/contractors/${sourceId}/merge`, { method: "POST", body: JSON.stringify({ targetContractorCompanyId }) });
  }

  contractorHistory(contractorCompanyId: string): Promise<ContractorHistoryRecord> {
    return this.request<ContractorHistoryRecord>(`/v1/contractors/${contractorCompanyId}/history`);
  }

  permanentlyDeleteArchivedProject(projectId: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>(`/v1/archive/projects/${projectId}`, { method: "DELETE" });
  }

  uploadDrawing(projectId: string, file: File, title: string, revision: string) {
    const form = new FormData();
    form.append("title", title);
    form.append("revision", revision);
    form.append("file", file, file.name);
    return this.request<Bootstrap["drawings"][number]>(`/v1/projects/${projectId}/drawings`, { method: "POST", body: form });
  }

  transcribeVideo(projectId: string, file: File, signal: AbortSignal): Promise<{ text: string; message: string }> {
    const form = new FormData(); form.append("file", file, file.name);
    return this.request(`/v1/projects/${projectId}/video-transcript`, { method: "POST", body: form, signal });
  }

  uploadEvidence(input: { clientId: string; projectId: string; snagId: string; kind: EvidenceKind; capturedAt: string; file: File }) {
    const form = new FormData();
    form.append("clientId", input.clientId);
    form.append("projectId", input.projectId);
    form.append("snagId", input.snagId);
    form.append("kind", input.kind);
    form.append("capturedAt", input.capturedAt);
    form.append("file", input.file, input.file.name);
    return this.request<Evidence>("/v1/evidence", { method: "POST", body: form });
  }

  async snagComments(snagId: string, input?: AddComment): Promise<SnagComment[]> {
    const path = `/v1/snags/${snagId}/comments`, owner = this.ownerId, identity = this.identityEpoch;
    if (!input) return this.offlineRead(path);
    const result = await this.request<SnagComment[]>(path, { method: 'POST', body: JSON.stringify(input) });
    if (!this.isViewingAs() && owner && identity === this.identityEpoch) await cacheValue(owner, path, result);
    return result;
  }
  companyInvitationPolicy(projectId: string, companyId: string, action?: "block" | "replace"): Promise<{ blocked: boolean; generation: string }> {
    return this.request(`/v1/projects/${projectId}/contractors/${companyId}/invitation-link`, action ? { method: "POST", body: JSON.stringify({ action }) } : {});
  }

  assignSnag(snagId: string, contractorCompanyId: string | null): Promise<Snag> {
    return this.request<Snag>(`/v1/snags/${snagId}/assignment`, {
      method: "PATCH",
      body: JSON.stringify({ contractorCompanyId }),
    });
  }

  updateSnag(snagId: string, changes: {
    dueDate?: string | null;
    contractorCompanyId?: string | null;
    priority?: Snag["priority"];
    locationId?: string | null;
    drawingId?: string | null;
    drawingPage?: number | null;
    markupX?: number | null;
    markupY?: number | null;
  }): Promise<Snag> {
    return this.request<Snag>(`/v1/snags/${snagId}`, {
      method: "PATCH",
      body: JSON.stringify(changes),
    });
  }

  setSnagRecordState(snagId: string, recordState: Snag["recordState"]): Promise<Snag> {
    return this.request<Snag>(`/v1/snags/${snagId}/record-state`, {
      method: "PATCH",
      body: JSON.stringify({ recordState }),
    });
  }

  async archivedSnags(projectId?: string): Promise<Snag[]> {
    const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : "";
    return (await this.request<{ snags: Snag[] }>(`/v1/archive/snags${query}`)).snags;
  }

  permanentlyDeleteArchivedSnag(snagId: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>(`/v1/archive/snags/${snagId}`, { method: "DELETE" });
  }

  evidenceBlob(evidenceId: string): Promise<Blob> {
    return this.file(`/v1/evidence/${evidenceId}/file`);
  }

  drawingBlob(drawingId: string): Promise<Blob> {
    return this.file(`/v1/drawings/${drawingId}/file`);
  }

  async push(input: SyncPush) {
    return this.request<{ results: Array<{ operationId: string; status: string; entityId: string | null; serverVersion: number | null; message: string }> }>("/v1/sync/push", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  notifications(query: NotificationQuery): Promise<NotificationFeed> {
    const params = new URLSearchParams({ unread: String(query.unread), ...(query.cursor ? { cursor: query.cursor } : {}) });
    return this.request(`/v1/notifications?${params}`);
  }
  readNotifications(ids: string[], readAt?: string): Promise<{ readIds: string[] }> {
    return this.request('/v1/notifications/read', { method: 'POST', body: JSON.stringify({ ids, ...(readAt ? { readAt } : {}) }) });
  }

  private async request<T>(path: string, init: RequestInit = {}, authenticated = true, retry = true): Promise<T> {
    const identity = this.identityEpoch;
    const isForm = typeof FormData !== "undefined" && init.body instanceof FormData;
    const hasBody = init.body !== undefined && init.body !== null;
    if (disconnected()) throw new NetworkError('Offline. Changes are saved in this browser until a connection returns.');
    const response = await fetch(`${API_URL}${path}`, {
      ...init,
      cache: "no-store",
      signal: init.signal ?? AbortSignal.timeout(isForm ? 300_000 : 45_000),
      headers: {
        ...(!isForm && hasBody ? { "Content-Type": "application/json" } : {}),
        ...(authenticated && this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        ...init.headers,
        ...this.previewHeaders(),
      },
    }).catch((reason: unknown) => { throw new NetworkError(reason instanceof Error ? reason.message : 'The server could not be reached'); });
    if (authenticated && identity !== this.identityEpoch) throw new ApiError("The signed-in account changed. Please retry with the original account.",401);
    if (response.status === 401 && authenticated && retry && await this.refresh()) { if(identity !== this.identityEpoch) throw new ApiError("The signed-in account changed.",401); return this.request<T>(path, init, authenticated, false); }
    const result = await response.json().catch(() => { throw new ApiError(`The server returned an unreadable response (HTTP ${response.status}).`, response.status >= 400 ? response.status : 502); }) as T & { error?: string; code?: string; account?: User };
    if (!response.ok) {
      if (response.status === 401) this.signOut();
      throw new ApiError(result.error ?? "The request could not be completed", response.status, result.code, result.account);
    }
    return result;
  }

  private async file(path: string, retry = true): Promise<Blob> {
    const owner = this.ownerId, identity = this.identityEpoch;
    if (!this.isSignedIn()) throw new ApiError('Sign in to view this file.', 401);
    const immutable = !this.isViewingAs() && (path.startsWith('/v1/evidence/') || path.startsWith('/v1/drawings/'));
    const cached = immutable ? await cachedValue<Blob>(owner, path) : null;
    if (identity !== this.identityEpoch) throw new ApiError('The signed-in account changed.', 401);
    if (cached) return cached;
    if (disconnected()) throw new NetworkError('This file has not finished saving for offline use. Open the project when connected.');
    const response = await fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${this.accessToken}`, ...this.previewHeaders() }, cache: "no-store", signal: AbortSignal.timeout(90_000) }).catch((reason: unknown) => { throw new NetworkError(reason instanceof Error ? reason.message : 'The file could not be reached'); });
    if (identity !== this.identityEpoch) throw new ApiError('The signed-in account changed.', 401);
    if (response.status === 401 && retry && await this.refresh()) return this.file(path, false);
    if (!response.ok) throw new ApiError('The file could not be opened', response.status);
    const blob = await response.blob();
    if (identity !== this.identityEpoch) throw new ApiError('The signed-in account changed.', 401);
    if(immutable) await cacheValue(owner, path, blob);
    return blob;
  }

  private async refresh(): Promise<boolean> {
    if (!this.refreshInFlight) this.refreshInFlight = this.refreshSession().finally(() => { this.refreshInFlight = null; });
    return this.refreshInFlight;
  }

  private async refreshSession(): Promise<boolean> {
    if (!this.refreshToken) return false;
    const generation = this.sessionGeneration;
    const response = await fetch(`${API_URL}/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: this.refreshToken }),
      signal: AbortSignal.timeout(30_000),
    }).catch((reason: unknown) => { throw new NetworkError(reason instanceof Error ? reason.message : 'Sign-in could not reconnect'); });
    if (generation !== this.sessionGeneration) return false;
    if (!response.ok) { if ([400, 401, 403].includes(response.status)) this.signOut(); else throw new ApiError('Sign-in could not reconnect. Your saved work is retained.', response.status); return false; }
    const session = await response.json() as Session;
    if (generation !== this.sessionGeneration) return false;
    this.sessionGeneration += 1;
    this.accessToken = session.accessToken;
    this.refreshToken = session.refreshToken;
    sessionStorage.setItem("site-audit-access-token", session.accessToken);
    sessionStorage.setItem("site-audit-refresh-token", session.refreshToken);
    return true;
  }
}

export const api = new ApiClient();
